import {
	AfterViewChecked,
	Directive,
	ElementRef,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	Renderer2,
	SimpleChanges
} from '@angular/core';

export interface TrendsComparisonCardConfig {
	/** Component property name that holds this card's chart options, e.g. 'generalChartOptions'. */
	key: string;

	/** Component @ViewChild property name for the current/original chart ref, e.g. 'generalTrendsChart'. */
	chartRefKey?: string;

	/** Optional direct chart ref, supported for pages where the ref is in scope. */
	currentChartRef?: any;
}

export interface TrendsComparisonDeltaItem {
	label: string;
	change: number;
	diff?: number;
	polarity?: 'positive' | 'negative' | 'neutral';
}

export interface TrendsComparisonChartPayload {
	comparisonKey: string;
	currentTitle: string;
	comparedTitle: string;
	comparedOptions: any;
	deltas: TrendsComparisonDeltaItem[];
	isHorizontalCategory: boolean;
	loading: boolean;
	error?: boolean;
	chartClass?: string;
}

@Directive({
	selector: '[appTrendsComparisonCard]'
})
export class TrendsComparisonCardDirective implements OnChanges, OnDestroy, AfterViewChecked {
	@Input('appTrendsComparisonCard') config: TrendsComparisonCardConfig;

	private rendererComponent: any = null;
	private latestPayload: TrendsComparisonChartPayload = null;
	private latestPatchOptions: any = null;
	private latestRestoreOptions: any = null;
	private lastAppliedChartRef: any = null;
	private applyRetryRaf: any = null;
	private active = false;
	private restoreWasApplied = false;
	private detectedChartClass = '';
	private originalHostStyles: {width: string; flex: string; maxWidth: string; order: string} = null;
	private bothEmptyEl: HTMLElement = null;
	private leftTitleEl = null;
	private rightTitleEl = null;
	private leftChartEl: HTMLElement = null;
	private rightTitleTimeout: any = null;
	private leftTitleTimeout: any = null;

	constructor(private el: ElementRef,
				private renderer: Renderer2,
				private ngZone: NgZone) {
	}

	get comparisonKey() {
		return this.config?.key;
	}

	get chartRefKey() {
		return this.config?.chartRefKey;
	}

	get chartClass(): string {
		return this.detectedChartClass || 'f-1-0 animated fadeIn';
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.config && !changes.config.firstChange) {
			// Dynamic cards can switch between two chart option keys/refs (for example pie/bars or all/executives).
			// Clear the renderer immediately; the top-level directive will detect the key/ref change and push a fresh payload.
			this.clearPayload();
			if (this.active && this.latestPatchOptions) {
				this.applyCurrentChartPatch(this.latestPatchOptions, this.latestRestoreOptions);
			}
		}
	}

	ngAfterViewChecked(): void {
		this.detectCurrentChartClass();
	}

	ngOnDestroy() {
		this.removeTitles();
		this.restoreCurrentChart();
		this.restoreCardStyles();
		this.setBothEmpty(false);
		this.rendererComponent?.clear();
		if (this.applyRetryRaf) {
			cancelAnimationFrame(this.applyRetryRaf);
			this.applyRetryRaf = null;
		}
	}

	setRenderer(component: any) {
		this.rendererComponent = component;
		if (this.latestPayload) {
			this.rendererComponent?.setPayload(this.latestPayload);
		}
	}

	setActive(active: boolean) {
		this.active = active;
		if (active) {
			this.restoreWasApplied = false;
			this.storeCardStyles();
			this.renderer.addClass(this.el.nativeElement, 'comparison-card-active');
			this.el.nativeElement.style.width = '100%';
			this.el.nativeElement.style.maxWidth = '100%';
			this.el.nativeElement.style.flex = '0 0 100%';
		}
		else {
			this.renderer.removeClass(this.el.nativeElement, 'comparison-card-active');
			this.clearPayload();
			this.restoreCurrentChart();
			this.restoreCardStyles();
			this.setBothEmpty(false);
		}
	}

	setLoading(loading: boolean) {
		this.rendererComponent?.setLoading(loading);
	}

	setPayload(payload: TrendsComparisonChartPayload) {
		this.detectCurrentChartClass();
		this.latestPayload = {
			...payload,
			chartClass: payload?.chartClass || this.chartClass
		};
		this.rendererComponent?.setPayload(this.latestPayload);
	}

	clearPayload() {
		this.latestPayload = null;
		this.rendererComponent?.clear();
		this.removeTitles();
	}

	getCurrentChartRef(host?: any) {
		if (this.config?.currentChartRef) {
			return this.config.currentChartRef;
		}
		if (this.config?.chartRefKey && host) {
			return host[this.config.chartRefKey];
		}
		return null;
	}

	applyCurrentChartPatch(patchOptions: any, restoreOptions: any, host?: any): boolean {
		this.latestPatchOptions = patchOptions;
		// Keep the first restore snapshot during the active comparison run.
		// Batches may produce new patchOptions, but restore should go back to the chart's normal-mode options.
		if (!this.latestRestoreOptions || this.restoreWasApplied) {
			this.latestRestoreOptions = restoreOptions;
			this.restoreWasApplied = false;
		}
		return this.tryApplyCurrentChartPatch(host, 0);
	}

	restoreCurrentChart(host?: any) {
		if (this.applyRetryRaf) {
			cancelAnimationFrame(this.applyRetryRaf);
			this.applyRetryRaf = null;
		}

		const chartRef = this.getCurrentChartRef(host) || this.lastAppliedChartRef;
		if (this.latestRestoreOptions && chartRef?.updateOptions) {
			try {
				chartRef.updateOptions(this.latestRestoreOptions, false, false);
				this.restoreWasApplied = true;
			}
			catch (e) {}
		}
		this.latestPatchOptions = null;
		this.latestRestoreOptions = null;
		this.lastAppliedChartRef = null;
	}


	detectCurrentChartClass(): void {
		const hostElement: HTMLElement = this.el?.nativeElement;
		if (!hostElement) {
			return;
		}

		const activeChart = hostElement.querySelector('apx-chart:not(.comparison-compared-chart)') as HTMLElement;
		if (!activeChart) {
			return;
		}

		const className = activeChart.getAttribute('class') || '';
		if (!className) {
			return;
		}

		const normalizedClassName = className
			.split(/\s+/)
			.filter(Boolean)
			.filter(classItem => classItem !== 'ng-star-inserted' && classItem !== 'comparison-left-chart')
			.join(' ');

		if (normalizedClassName && normalizedClassName !== this.detectedChartClass) {
			this.detectedChartClass = normalizedClassName;
			if (this.latestPayload) {
				this.latestPayload = {...this.latestPayload, chartClass: this.chartClass};
				this.rendererComponent?.setPayload(this.latestPayload);
			}
		}
	}

	private tryApplyCurrentChartPatch(host: any, attempt: number): boolean {
		const chartRef = this.getCurrentChartRef(host);
		if (chartRef?.updateOptions && this.latestPatchOptions) {
			try {
				chartRef.updateOptions(this.latestPatchOptions, false, false);
				this.lastAppliedChartRef = chartRef;
				return true;
			}
			catch (e) {
				// fall through to retry; Apex can throw while Angular is rebuilding an *ngIf chart
			}
		}

		// The original chart can temporarily disappear because many existing cards use
		// *ngIf="options && !getTrendsDataInProcess". Retry for a few frames and then
		// leave the latest patch stored; the next batch/config change will try again.
		if (attempt < 10) {
			this.ngZone.runOutsideAngular(() => {
				this.applyRetryRaf = requestAnimationFrame(() => {
					this.applyRetryRaf = null;
					this.tryApplyCurrentChartPatch(host, attempt + 1);
				});
			});
		}
		return false;
	}

	injectTitles(currentTitle: string, comparedTitle: string) {
		this.injectLeftTitle(currentTitle);
		if (this.rightTitleTimeout) {
			clearTimeout(this.rightTitleTimeout);
		}
		this.ngZone.runOutsideAngular(() => {
			this.rightTitleTimeout = setTimeout(() => {
				this.rightTitleTimeout = null;
				const rightChartEl = this.el.nativeElement.querySelector('apx-chart.comparison-compared-chart');
				if (rightChartEl) {
					this.ngZone.run(() => {
						this.upsertChartTitle(rightChartEl, comparedTitle, 'right');
					});
				}
			}, 50);
		});
	}

	// CC: separated so it can be retried after Angular re-renders (e.g. when *ngIf flips true due to host mutation)
	private injectLeftTitle(currentTitle: string) {
		if (this.leftTitleTimeout) {
			clearTimeout(this.leftTitleTimeout);
			this.leftTitleTimeout = null;
		}
		let leftChartEl = this.el.nativeElement.querySelector('apx-chart:not(.comparison-compared-chart)');
		// CC: fallback when left apx-chart is not in DOM (e.g. no data — *ngIf is false)
		// inject the period title into the host's "no data" placeholder instead.
		// skip wrapper divs whose apx-chart was removed by *ngIf (they have no element children left)
		if (!leftChartEl) {
			const trendsCard = this.el.nativeElement.querySelector('.trends-data-card');
			if (trendsCard) {
				const kids = Array.from(trendsCard.children) as HTMLElement[];
				leftChartEl = (kids.slice(1).find((el: HTMLElement) => {
						const tag = el.tagName.toLowerCase();
						if (tag === 'trends-comparison-chart' || el.classList.contains('trends-card-actions-menu')) {
							return false;
						}
						// CC: skip wrapper divs that contain only Angular comment anchors (apx-chart removed by *ngIf)
						return Array.from(el.childNodes).some(n => n.nodeType === Node.ELEMENT_NODE);
					}) as HTMLElement) || null;
			}
		}
		this.upsertChartTitle(leftChartEl, currentTitle, 'left');
		// CC: retry after Angular has had a chance to render the chart (e.g. after host mutation unblocks *ngIf)
		this.ngZone.runOutsideAngular(() => {
			this.leftTitleTimeout = setTimeout(() => {
				this.leftTitleTimeout = null;
				const retryChartEl = this.el.nativeElement.querySelector('apx-chart:not(.comparison-compared-chart)');
				if (retryChartEl) {
					this.ngZone.run(() => {
						this.upsertChartTitle(retryChartEl, currentTitle, 'left');
					});
				}
			}, 100);
		});
	}

	private upsertChartTitle(chartNativeEl: any, text: string, side: string) {
		if (!chartNativeEl) {
			return;
		}
		this.renderer.setStyle(chartNativeEl, 'position', 'relative');
		const existing = side === 'left' ? this.leftTitleEl : this.rightTitleEl;
		if (existing && existing.parentElement === chartNativeEl) {
			// title already injected into this element — just update the text
			existing.textContent = text;
			return;
		}
		if (existing) {
			existing.parentElement?.removeChild(existing);
		}
		const titleEl = this.renderer.createElement('div');
		this.renderer.setStyle(titleEl, 'position', 'absolute');
		this.renderer.setStyle(titleEl, 'top', '0');
		this.renderer.setStyle(titleEl, 'left', '0');
		this.renderer.setStyle(titleEl, 'right', '0');
		this.renderer.setStyle(titleEl, 'z-index', '5');
		this.renderer.setStyle(titleEl, 'pointer-events', 'none');
		this.renderer.setStyle(titleEl, 'font-weight', '600');
		this.renderer.setStyle(titleEl, 'color', 'var(--bs-primary)');
		this.renderer.setStyle(titleEl, 'font-size', '0.8rem');
		this.renderer.setStyle(titleEl, 'text-align', 'center');
		this.renderer.setStyle(titleEl, 'padding', '8px 10px 6px');
		const textNode = this.renderer.createText(text);
		this.renderer.appendChild(titleEl, textNode);
		this.renderer.insertBefore(chartNativeEl, titleEl, chartNativeEl.firstChild);
		if (side === 'left') {
			this.leftTitleEl = titleEl;
			if (this.leftChartEl !== chartNativeEl) {
				if (this.leftChartEl) {
					this.renderer.removeClass(this.leftChartEl, 'comparison-left-chart');
					this.renderer.removeClass(this.leftChartEl, 'horizontal-bar-chart');
				}
				this.renderer.addClass(chartNativeEl, 'comparison-left-chart');
				if (this.latestPayload?.isHorizontalCategory) {
					this.renderer.addClass(chartNativeEl, 'horizontal-bar-chart');
				}
				this.leftChartEl = chartNativeEl;
			}
		}
		else {
			this.rightTitleEl = titleEl;
		}
	}

	private removeTitles() {
		if (this.leftTitleTimeout) {
			clearTimeout(this.leftTitleTimeout);
			this.leftTitleTimeout = null;
		}
		if (this.rightTitleTimeout) {
			clearTimeout(this.rightTitleTimeout);
			this.rightTitleTimeout = null;
		}
		if (this.leftTitleEl) {
			this.leftTitleEl.parentElement?.removeChild(this.leftTitleEl);
			this.leftTitleEl = null;
		}
		if (this.leftChartEl) {
			this.renderer.removeClass(this.leftChartEl, 'comparison-left-chart');
			this.renderer.removeClass(this.leftChartEl, 'horizontal-bar-chart');
			this.leftChartEl = null;
		}
		if (this.rightTitleEl) {
			this.rightTitleEl.parentElement?.removeChild(this.rightTitleEl);
			this.rightTitleEl = null;
		}
	}

	setBothEmpty(bothEmpty: boolean) {
		const hostEl = this.el.nativeElement;
		const trendsDataCard = hostEl.querySelector('.trends-data-card');
		if (!trendsDataCard) {
			return;
		}
		if (!bothEmpty) {
			if (this.bothEmptyEl) {
				this.bothEmptyEl.remove();
				this.bothEmptyEl = null;
			}
			this.renderer.removeClass(trendsDataCard, 'comparison-both-empty');
			return;
		}
		this.renderer.addClass(trendsDataCard, 'comparison-both-empty');
		if (this.bothEmptyEl) {
			return;
		}
		const label = this.renderer.createElement('div');
		this.renderer.addClass(label, 'comparison-both-empty-label');
		this.renderer.setStyle(label, 'grid-column', '1 / -1');
		this.renderer.setStyle(label, 'display', 'flex');
		this.renderer.setStyle(label, 'align-items', 'center');
		this.renderer.setStyle(label, 'justify-content', 'center');
		this.renderer.setStyle(label, 'font-weight', '600');
		this.renderer.setStyle(label, 'opacity', '0.7');
		this.renderer.setStyle(label, 'font-size', '0.85rem');
		const text = this.renderer.createText('No data for selected periods');
		this.renderer.appendChild(label, text);
		this.renderer.appendChild(trendsDataCard, label);
		this.bothEmptyEl = label;
	}

	private storeCardStyles() {
		if (this.originalHostStyles) {
			return;
		}
		const style = this.el.nativeElement.style;
		this.originalHostStyles = {
			width: style.width || '',
			flex: style.flex || '',
			maxWidth: style.maxWidth || '',
			order: style.order || ''
		};
	}

	private restoreCardStyles() {
		if (!this.originalHostStyles) {
			return;
		}
		const style = this.el.nativeElement.style;
		style.width = this.originalHostStyles.width;
		style.flex = this.originalHostStyles.flex;
		style.maxWidth = this.originalHostStyles.maxWidth;
		style.order = this.originalHostStyles.order;
		this.originalHostStyles = null;
	}
}
